/**
 * @fileoverview Report Navigation Context - Provides page navigation capability
 * to deeply nested block renderers in multi-page reports.
 *
 * This context allows table blocks (or any future block) to navigate between
 * pages without prop drilling through the rendering chain.
 *
 * Usage:
 * - InsightCardMultiPageRenderer wraps page content with ReportNavigationProvider
 * - Block renderers (e.g., DataTableClientBlockRenderer) consume useReportNavigation()
 * - Returns null when not inside a multi-page report — navigation is optional
 *
 * To add page-link support to a new block type:
 * 1. Call useReportNavigation() in your block renderer
 * 2. Check for non-null return value (null = not in a multi-page report)
 * 3. Call navigateToPage({ page_group_id, page_id }) to switch pages
 */

import { createContext, useContext } from 'react';
import type { PageRef } from '@/types/ui-blocks';

interface ReportNavigationContextValue {
  /** Navigate to a specific page identified by group ID and page ID */
  navigateToPage: (ref: PageRef) => void;
}

const ReportNavigationContext = createContext<ReportNavigationContextValue | null>(null);

export const ReportNavigationProvider: React.FC<{
  children: React.ReactNode;
  navigateToPage: (ref: PageRef) => void;
}> = ({ children, navigateToPage }) => {
  return (
    <ReportNavigationContext.Provider value={{ navigateToPage }}>
      {children}
    </ReportNavigationContext.Provider>
  );
};

/**
 * Returns the navigation context or null if not within a multi-page report.
 * Does NOT throw — consumers should check for null before using.
 */
export const useReportNavigation = (): ReportNavigationContextValue | null => {
  return useContext(ReportNavigationContext);
};
